<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="flex justify-between items-start">
            <div>
              <h3 class="text-xl font-bold text-gray-900">
                Revenue Forecast Template
              </h3>
              <p class="text-sm text-gray-500 mt-1">Template for forecasting and planning purposes</p>
            </div>
            
            <!-- Progress Steps -->
            <div class="flex items-center space-x-2 mx-4">
              <div class="flex flex-col items-center">
                <div class="h-6 w-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">1</div>
                <span class="text-xs mt-1">Creating</span>
              </div>
              <div class="w-4 h-0.5 bg-blue-600"></div>
              <div class="flex flex-col items-center">
                <div class="h-6 w-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">2</div>
                <span class="text-xs mt-1">Pending Review</span>
              </div>
              <div class="w-4 h-0.5 bg-gray-200"></div>
              <div class="flex flex-col items-center">
                <div class="h-6 w-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-xs">4</div>
                <span class="text-xs mt-1">Email Sent</span>
              </div>
            </div>
            
            <button @click="close" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Template Information -->
            <div>
              <h4 class="font-medium mb-3">Template Information</h4>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-gray-500">Modified:</span>
                  <span>2025-04-02</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Format:</span>
                  <span>Excel (.xlsx)</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Size:</span>
                  <span>245 KB</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Status:</span>
                  <span class="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">Email Pending</span>
                </div>
              </div>
            </div>
            
            <!-- Planner Information -->
            <div>
              <h4 class="font-medium mb-3">Planner</h4>
              <div class="flex items-center">
                <div class="h-10 w-10 rounded-full bg-gray-200 flex-shrink-0"></div>
                <div class="ml-3">
                  <p class="font-medium">Luka Novak</p>
                  <p class="text-sm text-gray-500">Financial Planner</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Email Template Preview -->
          <div class="mt-8">
            <div class="flex justify-between items-center mb-4">
              <div class="flex items-center">
                <h4 class="font-medium">Email Template Preview</h4>
                <span
                  class="ml-2 text-xs px-2 py-0.5 rounded-full"
                  :class="isEditMode ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'"
                >
                  {{ isEditMode ? 'Editable' : 'Read-only' }}
                </span>
              </div>
              <button
                @click="toggleEditMode"
                class="text-sm px-3 py-1 rounded-md border"
                :class="isEditMode
                  ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                  : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'"
              >
                {{ isEditMode ? 'Disable Editing' : 'Enable Editing' }}
              </button>
            </div>
            
            <!-- Loading and error states -->
            <div v-if="isLoading" class="flex justify-center items-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span class="ml-2">Loading template...</span>
            </div>
            
            <div v-else-if="errorMessage" class="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm text-red-700">{{ errorMessage }}</p>
                </div>
              </div>
            </div>
            
            <div
              v-else
              class="space-y-4 bg-white border-2 rounded-md p-4 transition-colors duration-200"
              :class="isEditMode ? 'border-blue-300' : 'border-gray-200'"
            >
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="email"
                  v-model="emailData.to"
                  :class="[
                    'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none',
                    isEditMode
                      ? 'border-blue-300 focus:ring-blue-500 focus:border-blue-500 bg-white'
                      : 'border-gray-200 bg-gray-50 cursor-default'
                  ]"
                  :readonly="!isEditMode"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  v-model="emailData.subject"
                  :class="[
                    'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none',
                    isEditMode
                      ? 'border-blue-300 focus:ring-blue-500 focus:border-blue-500 bg-white'
                      : 'border-gray-200 bg-gray-50 cursor-default'
                  ]"
                  :readonly="!isEditMode"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Message</label>
                
                <!-- Rich Text Editor Toolbar (only visible in edit mode) -->
                <div v-if="isEditMode" class="border border-gray-300 rounded-t-md bg-gray-50 p-2 flex flex-wrap gap-1">
                  <button
                    @click.prevent="applyFormatting('bold')"
                    class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
                    title="Bold"
                  >
                    <span class="font-bold text-lg">B</span>
                  </button>
                  <button
                    @click.prevent="applyFormatting('italic')"
                    class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
                    title="Italic"
                  >
                    <span class="italic text-lg">i</span>
                  </button>
                  <button
                    @click.prevent="applyFormatting('underline')"
                    class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
                    title="Underline"
                  >
                    <span class="underline text-lg">U</span>
                  </button>
                </div>
                
                <!-- HTML content display when in readonly mode -->
                <div 
                  v-if="!isEditMode"
                  class="prose max-w-none w-full px-3 py-2 border rounded-md shadow-sm overflow-auto border-gray-200 bg-gray-50 cursor-default h-64"
                  v-html="sanitizedMessageHtml"
                ></div>
                
                <!-- Rich Text Editor Content Area when in edit mode -->
                <div
                  v-else
                  id="email-content-editor"
                  contenteditable="true"
                  @input="updateEmailContent"
                  class="w-full min-h-[250px] px-3 py-2 border border-gray-300 rounded-b-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between">
          <div>
            <button
              @click="navigateToEditTemplate"
              class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
            >
              Edit Template
            </button>
          </div>
          <button
            @click="sendEmail"
            :disabled="isLoading"
            class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isLoading ? 'Sending...' : 'Send Email' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, onMounted, computed, nextTick } from 'vue';
import { supabase } from '@/services/supabase';
import { logger } from '@/services/logger';

interface EmailTemplate {
  id: string; // UUID is stored as string in JavaScript
  subject: string;
  body: string;
  name: string;
}

interface ProcessStep {
  id: string | null; // Can be null for process steps without IDs
  name: string;
  description: string;
  order_number: number;
}

export default defineComponent({
  name: 'EmailReviewModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    processStepType: {
      type: String,
      required: false,
      default: null
    },
    recipientEmail: {
      type: String,
      required: false,
      default: ''
    },
    templateVariables: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  emits: ['close', 'send-success'],
  setup(props, { emit }) {
    const isEditMode = ref(false);
    const isLoading = ref(true);
    const errorMessage = ref('');
    const currentTemplate = ref<EmailTemplate | null>(null);
    const currentProcessStep = ref<ProcessStep | null>(null);
    // Track if the content is HTML - assume HTML by default
    const isHtmlContent = ref(true);
    
    const emailData = ref({
      to: props.recipientEmail || 'luka.novak@company.com',
      subject: '',
      message: ''
    });
    
    // Main function to load the appropriate template based on the process step
    async function loadTemplateForProcessStep() {
      isLoading.value = true;
      errorMessage.value = '';
      
      try {
        if (!props.processStepType) {
          // If no process step type is provided, load a default template
          setDefaultTemplate();
          return;
        }
        
        // Set current process step
        setCurrentProcessStep();
        
        const mapping = await getTemplateMapping();
        if (!mapping) {
          return;
        }
        
        const template = await getEmailTemplate(mapping.email_template_id);
        if (!template) {
          return;
        }

        currentTemplate.value = template;
        
        processTemplateContentWithVariables(template);
        
        
      } catch (error: any) {
        handleTemplateError(error);
      } finally {
        isLoading.value = false;
      }
    }
    
    // Set the current process step
    function setCurrentProcessStep() {
      currentProcessStep.value = {
        id: null,
        name: props.processStepType,
        description: '',
        order_number: 0
      };
    }
    
    // Get the template mapping for the current process step
    async function getTemplateMapping(): Promise<any> {
      const { data: mapping, error: mappingError } = await supabase
        .from('email_template_process_mappings')
        .select('email_template_id')
        .eq('process_step_type', props.processStepType)
        .eq('is_active', true)
        .single();
        
      if (mappingError && mappingError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        throw new Error(`Error fetching template mapping: ${mappingError.message}`);
      }
      
      if (!mapping) {
        // No mapping found, use default template
        logger.info(`No template mapping found for process step ${props.processStepType}, using default`);
        setDefaultTemplate();
        return null;
      }
      
      return mapping;
    }
    
    // Get the email template by ID
    async function getEmailTemplate(templateId: string): Promise<any> {
      const { data: template, error: templateError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', templateId)
        .single();
        
      if (templateError) {
        throw new Error(`Error fetching email template: ${templateError.message}`);
      }
      
      if (!template) {
        throw new Error(`Email template with ID ${templateId} not found`);
      }
      
      currentTemplate.value = template;
      return template;
    }
    
    // Process the template content
    function processTemplateContentWithVariables(template: any): void {
      // Log the template data for debugging
      console.log('Template loaded:', template);
      console.log('Template keys:', Object.keys(template));
      console.log('Template subject:', template.subject);
      
      // Extract message content
      const messageContent = extractMessageContent(template);
      
      let processedSubject = template.subject || '';
      let processedContent = messageContent || '';
      
      // Replace variables in subject and content
      if (props.templateVariables && Object.keys(props.templateVariables).length > 0) {
        console.log('Processing template with variables:', props.templateVariables);
        
        processedSubject = replaceVariables(processedSubject, props.templateVariables);
        processedContent = replaceVariables(processedContent, props.templateVariables);
      }
      
      setEmailData({
        ...template,
        subject: processedSubject
      }, processedContent);
    }
    
    // Extract message content from template
    function extractMessageContent(template: any): string {
      // Check for different possible column names for the message body
      const messageContent = template.body || template.content || template.message || template.text || '';
      console.log('Message content found:', messageContent);
      console.log('Message content type:', typeof messageContent);
      
      // Try to parse JSON content
      if (typeof messageContent === 'string' && messageContent.trim().startsWith('{')) {
        const jsonContent = tryParseJsonContent(messageContent);
        if (jsonContent) {
          return jsonContent;
        }
      }
      
      // Check if content is HTML
      checkIfContentIsHtml(messageContent);
      
      return messageContent;
    }
    
    // Try to parse JSON content
    function tryParseJsonContent(content: string): string | null {
      try {
        const parsedContent = JSON.parse(content);
        console.log('Parsed JSON content:', parsedContent);
        
        if (parsedContent.html) {
          console.log('Found HTML content in JSON:', parsedContent.html);
          emailData.value.message = parsedContent.html;
          isHtmlContent.value = true;
          return parsedContent.html;
        }
      } catch (e) {
        console.log('Not valid JSON, using as is', e);
      }
      
      return null;
    }
    
    // Check if content is HTML
    function checkIfContentIsHtml(content: string): void {
      if (typeof content === 'string' &&
          (content.includes('<html') ||
           content.includes('<body') ||
           content.includes('<div') ||
           content.includes('<p'))) {
        console.log('Content appears to be HTML');
        isHtmlContent.value = true;
      }
    }
    
    // Set email data from template and content
    function setEmailData(template: any, messageContent: string | null): void {
      // Set subject
      emailData.value.subject = template.subject || 'No subject found';
      
      // Set message content
      if (messageContent) {
        emailData.value.message = messageContent;
      } else {
        console.warn('No message content found in template, using fallback');
        setFallbackMessage();
      }
    }
    
    // Set fallback message
    function setFallbackMessage() {
      emailData.value.message = `Dear ${props.recipientEmail.split('@')[0]},

This is a fallback message because the template content could not be loaded.

Please check the template configuration in the database.

Best regards,
Financial Control Team`;
    }
    
    // Handle template loading error
    function handleTemplateError(error: any): void {
      logger.error('Error loading template for process step', { error });
      errorMessage.value = error?.message || 'Failed to load email template';
      setDefaultTemplate();
    }
    
    function setDefaultTemplate() {
      emailData.value = {
        to: props.recipientEmail || 'luka.novak@company.com',
        subject: 'Revenue Forecast Template for Q2 2025',
        message: `Dear Luka Novak,

Attached is the Revenue Forecast Template for Q2 2025. Please complete this template with your forecasted data by April 20, 2025.

If you have any questions, please don't hesitate to contact the Financial Control team.

Best regards,
Financial Control Team
Fraport AG`
      };
    }
    
    // Load the template when the component is mounted
    onMounted(() => {
      loadTemplateForProcessStep();
    });
    
    function close() {
      emit('close');
    }
    
    function navigateToEditTemplate() {
      close(); // Close the modal first
      // Use window.location to navigate
      window.location.href = '/administration/email-templates';
    }
    
    function toggleEditMode() {
      isEditMode.value = !isEditMode.value;
      
      // When switching to edit mode, initialize the rich text editor
      if (isEditMode.value) {
        nextTick(() => {
          const editorElement = document.getElementById('email-content-editor');
          if (editorElement) {
            editorElement.innerHTML = emailData.value.message;
          }
        });
      }
    }
    
    // Update email content from the rich text editor
    function updateEmailContent(event: Event) {
      const target = event.target as HTMLElement;
      emailData.value.message = target.innerHTML;
    }
    
    // Apply formatting to the rich text editor
    function applyFormatting(format: string) {
      document.execCommand('styleWithCSS', false, 'true');
      
      switch (format) {
        case 'bold':
          document.execCommand('bold', false);
          break;
        case 'italic':
          document.execCommand('italic', false);
          break;
        case 'underline':
          document.execCommand('underline', false);
          break;
      }
      
      // Update the email content after formatting
      const editorElement = document.getElementById('email-content-editor');
      if (editorElement) {
        emailData.value.message = editorElement.innerHTML;
      }
    }

    async function sendEmail() {
      try {
        // Here you would implement the actual email sending logic
        // For now, we'll just simulate a successful send
        logger.info('Sending email', { to: emailData.value.to, subject: emailData.value.subject });
        
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Emit success event
        emit('send-success');
        
        // Close the modal
        close();
      } catch (error: any) {
        logger.error('Error sending email', { error });
        errorMessage.value = error?.message || 'Failed to send email. Please try again.';
      }
    }
    
    // Computed property to determine if content has HTML tags
    const contentHasHtmlTags = computed(() => {
      if (!emailData.value || !emailData.value.message) {
        return false;
      }
      
      const content = emailData.value.message;
      return content.includes('<html') || 
             content.includes('<body') || 
             content.includes('<div') || 
             content.includes('<p') ||
             content.includes('<span') ||
             content.includes('<br');
    });
    
    // Computed property to sanitize HTML content
    const sanitizedMessageHtml = computed(() => {
      // In a production environment, you should use a proper HTML sanitizer like DOMPurify
      // This is a simple implementation for demonstration purposes
      if (!emailData.value || !emailData.value.message) {
        return ''; // Return empty string if message is undefined
      }
      
      const content = emailData.value.message;
      
      // If content has HTML tags or is marked as HTML, return as is
      if (isHtmlContent.value || contentHasHtmlTags.value) {
        return content;
      }
      
      // Otherwise, convert newlines to <br> tags
      return content
        .replace(/\n/g, '<br>')
        .trim();
    });
    
    function replaceVariables(text: string, variables: Record<string, any>): string {
      if (!text) return '';
      
      return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
        const trimmedName = variableName.trim();
        
        if (Object.prototype.hasOwnProperty.call(variables, trimmedName)) {
          const value = variables[trimmedName];
          return value !== null && value !== undefined ? String(value) : '';
        }
        
        console.warn(`Variable not found: ${trimmedName}`);
        return match;
      });
    }

    return {
      close,
      navigateToEditTemplate,
      isEditMode,
      isLoading,
      errorMessage,
      emailData,
      toggleEditMode,
      sendEmail,
      currentTemplate,
      currentProcessStep,
      sanitizedMessageHtml,
      isHtmlContent,
      contentHasHtmlTags,
      updateEmailContent,
      applyFormatting
    };
  }
});
</script>

<style scoped>
.prose {
  line-height: 1.6;
}
</style>
